import React, { useState } from 'react';
import {
  BookOpen, ChevronDown, ChevronUp, Rocket, Smartphone, Sparkles,
  Settings, Globe, Volume2, Sliders, FileText, Type, History,
  XCircle, VolumeX, Wifi, Pause, HelpCircle, Info, Link,
  Download, Cpu, Languages, Zap, Radio
} from 'lucide-react';
import './Instructions.css';

const ReceiverInstructions = () => {
  const [expandedSections, setExpandedSections] = useState({
    quickStart: true,
    features: false,
    troubleshooting: false,
    faq: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="instructions-container">
      <h2 className="instructions-title"><BookOpen className="icon-inline" size={28} /> Participant Guide</h2>

      <div className="instruction-section">
        <div
          className="section-header"
          onClick={() => toggleSection('quickStart')}
        >
          <h3><Rocket className="icon-inline" size={20} /> Quick Start Guide</h3>
          <span className={`collapse-icon ${expandedSections.quickStart ? 'expanded' : ''}`}>
            {expandedSections.quickStart ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </span>
        </div>
        {expandedSections.quickStart && (
          <div className="section-content">
            <div className="scenario-tabs">
              <h4><Link className="icon-inline" size={18} /> Most Common: Using a Shared Link (Recommended)</h4>
              <ol className="instruction-list">
                <li>
                  <strong>Get the Session Link from Your Host</strong>
                  <p>Your host will share a pre-configured link that looks like this:</p>
                  <code>{`${window.location.origin}/receiver?sessionId=12345&targetLanguage=es-ES&enableTts=true&password=mypassword`}</code>
                  <p className="note"><Sparkles className="icon-inline" size={14} /> All your settings are already configured by the host - language, audio preferences, and even AI model selection!</p>
                </li>
                <li>
                  <strong>Click the Link</strong>
                  <p>The link automatically takes you to the receiver page with all session details pre-filled.</p>
                </li>
                <li>
                  <strong>Join Instantly</strong>
                  <p>Click the large "Join Session" button to start receiving real-time translations!</p>
                  <p className="tip"><Info className="icon-inline" size={14} /> Click "Show Settings" if you want to make any adjustments (font size, etc.)</p>
                </li>
              </ol>
            </div>

            <div className="scenario-divider"></div>

            <div className="scenario-tabs">
              <h4><Settings className="icon-inline" size={18} /> Alternative: Manual Setup</h4>
              <ol className="instruction-list">
                <li>
                  <strong>Navigate to the Receiver Page</strong>
                  <p>Go directly to the receiver page without a pre-configured link.</p>
                </li>
                <li>
                  <strong>Configure Your Settings</strong>
                  <ul>
                    <li><strong>Session ID:</strong> Enter the session ID provided by your host</li>
                    <li><strong>Target Language:</strong> Select the language you want to receive translations in</li>
                    <li><strong>Password:</strong> Enter the session password (if required)</li>
                    <li><strong>Text-to-Speech:</strong> Check this to hear translations spoken aloud</li>
                    <li><strong>Font Size:</strong> Adjust for comfortable reading</li>
                  </ul>
                </li>
                <li>
                  <strong>Join the Session</strong>
                  <p>Click "Join Session" to start receiving real-time translations!</p>
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>

      <div className="instruction-section">
        <div
          className="section-header"
          onClick={() => toggleSection('features')}
        >
          <h3><Sparkles className="icon-inline" size={20} /> Features & Settings</h3>
          <span className={`collapse-icon ${expandedSections.features ? 'expanded' : ''}`}>
            {expandedSections.features ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </span>
        </div>
        {expandedSections.features && (
          <div className="section-content">

            {/* Group 1: Live Translation */}
            <div className="feature-group">
              <div className="feature-group-label"><Radio className="icon-inline" size={12} /> Live Translation</div>
              <div className="feature-grid">
                <div className="feature-item">
                  <h4><FileText className="icon-inline" size={18} /> Live Translation Display</h4>
                  <p>Translations appear in real-time with automatic scrolling to the latest entry.</p>
                  <ul>
                    <li>Final translations shown instantly after each sentence</li>
                    <li>Timestamps on every entry for easy reference</li>
                    <li>Scroll back to review earlier translations</li>
                  </ul>
                </div>

                <div className="feature-item">
                  <h4><Zap className="icon-inline" size={18} /> Instant Transcription</h4>
                  <p>Enable "Display Instant Transcription" in settings to see live in-progress text as the speaker talks — before the final translation is ready.</p>
                  <ul>
                    <li>Updates word-by-word in real time</li>
                    <li>Replaced automatically by the final translation</li>
                  </ul>
                  <p className="tip"><Info className="icon-inline" size={14} /> Enable before joining — the toggle is disabled while connected.</p>
                </div>

                <div className="feature-item">
                  <h4><Languages className="icon-inline" size={18} /> Show Original Text</h4>
                  <p>Toggle "Show Original" in settings to display the speaker's source-language text alongside each translation.</p>
                  <ul>
                    <li>Useful for bilingual participants verifying accuracy</li>
                    <li>Setting persists across page refreshes</li>
                  </ul>
                </div>

                <div className="feature-item">
                  <h4><History className="icon-inline" size={18} /> Session History</h4>
                  <p>All translations are displayed with timestamps. Scroll back at any time to review what was said earlier in the session.</p>
                </div>
              </div>
            </div>

            {/* Group 2: Audio & Interface */}
            <div className="feature-group">
              <div className="feature-group-label"><Volume2 className="icon-inline" size={12} /> Audio & Interface</div>
              <div className="feature-grid">
                <div className="feature-item">
                  <h4><Volume2 className="icon-inline" size={18} /> Text-to-Speech (TTS)</h4>
                  <p>Hear translations spoken aloud with natural-sounding voices. Usually pre-configured by your host.</p>
                  <ul>
                    <li>Works on all modern browsers</li>
                    <li>Automatic playback as translations arrive</li>
                    <li>Click "Enable Audio" banner if prompted by the browser</li>
                    <li><strong>iOS:</strong> Tap anywhere on screen after joining to unlock audio</li>
                  </ul>
                </div>

                <div className="feature-item">
                  <h4><Type className="icon-inline" size={18} /> Customizable Font Size</h4>
                  <p>Adjust text size from 12px to 32px for comfortable reading on any device. Available in the settings panel.</p>
                </div>

                <div className="feature-item">
                  <h4><Smartphone className="icon-inline" size={18} /> Mobile-Optimized</h4>
                  <p>Designed for smartphones and tablets with space-saving layout:</p>
                  <ul>
                    <li>Auto-collapsed settings form when using a shared link</li>
                    <li>Touch-friendly controls</li>
                    <li>Restore settings via "Show Settings" button at any time</li>
                  </ul>
                </div>

                <div className="feature-item">
                  <h4><Sliders className="icon-inline" size={18} /> Maximize Viewing Area</h4>
                  <p>Hide the control panel after joining to give translations the full screen:</p>
                  <ul>
                    <li>Click the minimize button (⚊) in the controls header</li>
                    <li>Restore via "⚙️ Controls" in the top navigation bar</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Group 3: Save & AI */}
            <div className="feature-group">
              <div className="feature-group-label"><Download className="icon-inline" size={12} /> Save & AI Features</div>
              <div className="feature-grid">
                <div className="feature-item">
                  <h4><Download className="icon-inline" size={18} /> Download Transcript</h4>
                  <p>Save the full session transcript to your device at any time — during or after the session.</p>
                  <ul>
                    <li>Click the <strong>Download</strong> button in the header bar (visible when controls are hidden)</li>
                    <li>Saves as a CSV file: <code>transcript_[sessionId]_[date].csv</code></li>
                    <li>Transcript is preserved even after the host ends the session</li>
                  </ul>
                </div>

                <div className="feature-item">
                  <h4><Cpu className="icon-inline" size={18} /> Meeting AI <span className="feature-badge">Zoom Plugin</span></h4>
                  <p>Participants joining via the <strong>Zoom Plugin</strong> with a Meeting AI-enabled plan get additional AI features:</p>
                  <ul>
                    <li><strong>AI Tab:</strong> Ask questions about the meeting and get AI-powered answers</li>
                    <li><strong>Transcript Tab:</strong> Browse the full transcript with speaker attribution, filter by speaker, download as TXT or JSONL</li>
                    <li><strong>Suggestion Chips:</strong> One-tap question templates pre-configured by your host</li>
                  </ul>
                  <p className="tip"><Info className="icon-inline" size={14} /> Meeting AI is gated by plan — contact your host or admin to confirm availability.</p>
                </div>

                <div className="feature-item">
                  <h4><Globe className="icon-inline" size={18} /> AI Translation Services</h4>
                  <p>Your host selects the AI service powering your translations. Options include:</p>
                  <ul>
                    <li><strong>OpenAI:</strong> GPT-5.2 (default), GPT-5.1, GPT-4o, and others</li>
                    <li><strong>Google Gemini:</strong> Gemini 2.5 Pro, 2.5 Flash, and others</li>
                    <li><strong>Google Translate:</strong> Traditional fast translation</li>
                    <li><strong>Others:</strong> DeepL, Azure AI, DeepSeek</li>
                  </ul>
                  <p className="tip"><Info className="icon-inline" size={14} /> Quality and speed vary by service — your host picks what works best for the session.</p>
                </div>
              </div>
            </div>

          </div>
        )}
      </div>

      <div className="instruction-section">
        <div
          className="section-header"
          onClick={() => toggleSection('troubleshooting')}
        >
          <h3><Settings className="icon-inline" size={20} /> Troubleshooting</h3>
          <span className={`collapse-icon ${expandedSections.troubleshooting ? 'expanded' : ''}`}>
            {expandedSections.troubleshooting ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </span>
        </div>
        {expandedSections.troubleshooting && (
          <div className="section-content">
            <div className="troubleshooting-list">
              <div className="trouble-item">
                <h4><XCircle className="icon-inline" size={18} /> "Session not found" error</h4>
                <ul>
                  <li>Verify the session ID is correct</li>
                  <li>Ensure the host has started the session</li>
                  <li>Check if the session has expired</li>
                  <li>Ask the host to share a new link</li>
                </ul>
              </div>

              <div className="trouble-item">
                <h4><VolumeX className="icon-inline" size={18} /> No audio playback (TTS enabled)</h4>
                <ul>
                  <li>Click "Enable Audio" when prompted</li>
                  <li>Check your device volume</li>
                  <li>Try refreshing the page</li>
                  <li>For iOS: Tap anywhere on the screen after joining</li>
                  <li>Ensure your browser supports Web Audio API</li>
                </ul>
              </div>

              <div className="trouble-item">
                <h4><Wifi className="icon-inline" size={18} /> Connection issues</h4>
                <ul>
                  <li>Check your internet connection</li>
                  <li>Try refreshing the page</li>
                  <li>Disable VPN if you're using one</li>
                  <li>Try a different browser</li>
                </ul>
              </div>

              <div className="trouble-item">
                <h4><Pause className="icon-inline" size={18} /> "Session Paused" message</h4>
                <p>The host has temporarily paused the translation. Wait for them to resume the session.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="instruction-section">
        <div
          className="section-header"
          onClick={() => toggleSection('faq')}
        >
          <h3><HelpCircle className="icon-inline" size={20} /> Frequently Asked Questions</h3>
          <span className={`collapse-icon ${expandedSections.faq ? 'expanded' : ''}`}>
            {expandedSections.faq ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </span>
        </div>
        {expandedSections.faq && (
          <div className="section-content">
            <div className="faq-list">
              <div className="faq-item">
                <h4>Do I need to install anything?</h4>
                <p>No! This works entirely in your web browser. Just click the link and join.</p>
              </div>

              <div className="faq-item">
                <h4>Can I join from my phone?</h4>
                <p>Yes! The app is fully mobile-responsive and works on iOS and Android devices.</p>
              </div>

              <div className="faq-item">
                <h4>Can I save the translations?</h4>
                <p>Yes! Click the <strong>Download</strong> button in the header bar to save the full session as a CSV file. The transcript is preserved even after the host ends the session, so you can download it any time before leaving the page.</p>
              </div>

              <div className="faq-item">
                <h4>What browsers are supported?</h4>
                <p>Chrome, Firefox, Safari, and Edge (latest versions). For best TTS support, we recommend Chrome or Edge.</p>
              </div>

              <div className="faq-item">
                <h4>Is there a delay in translations?</h4>
                <p>Translations typically appear within 1-3 seconds after the speaker finishes a sentence.</p>
              </div>

              <div className="faq-item">
                <h4>Can I join multiple sessions?</h4>
                <p>You can only join one session at a time per browser tab. Open multiple tabs for multiple sessions.</p>
              </div>

              <div className="faq-item">
                <h4>What if I miss part of the session?</h4>
                <p>You'll see all translations from the moment you join. Previous translations before you joined are not available.</p>
              </div>

              <div className="faq-item">
                <h4>Why do translation quality and speed vary?</h4>
                <p>Your host selects the AI translation service and model. Different services have different strengths:</p>
                <ul>
                  <li><strong>OpenAI (GPT-5.2, GPT-4o):</strong> High quality, context-aware translations</li>
                  <li><strong>Google Gemini:</strong> Fast, modern AI with good multilingual support (Gemini 2.5 Pro/Flash)</li>
                  <li><strong>Google Translate:</strong> Very fast, traditional service for common languages</li>
                  <li><strong>DeepL:</strong> Excellent for European languages</li>
                  <li><strong>Custom Models:</strong> Your host may test cutting-edge models</li>
                </ul>
              </div>

              <div className="faq-item">
                <h4>Can I see which AI service is being used?</h4>
                <p>The specific service and model are configured by your host. You'll experience the best quality they've selected for your session's needs.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="instructions-footer">
        <div className="pro-tips">
          <h4><Sparkles className="icon-inline" size={18} /> Quick Tips</h4>
          <div className="pro-tips-grid">
            <div className="pro-tip-card">
              <strong>Shared link?</strong> Everything is pre-configured — just click "Join Session".
            </div>
            <div className="pro-tip-card">
              <strong>On mobile?</strong> Hide controls after joining for a full-screen translation view.
            </div>
            <div className="pro-tip-card">
              <strong>On iOS with TTS?</strong> Tap anywhere on screen after joining to unlock audio playback.
            </div>
            <div className="pro-tip-card">
              <strong>Want bigger text?</strong> Adjust font size (12–32px) in the settings panel before joining.
            </div>
            <div className="pro-tip-card">
              <strong>Need a record?</strong> Download the transcript as CSV any time — it stays available after the session ends.
            </div>
            <div className="pro-tip-card">
              <strong>Red button</strong> = Leave Session. <strong>Download button</strong> = Save transcript. Don't mix them up!
            </div>
          </div>
        </div>
        <p className="footer-help">Need more help? Contact your session host for assistance.</p>
      </div>
    </div>
  );
};

export default ReceiverInstructions;
